import { StageBlock } from './types';

import * as whatIsEthereum from './stages/ethereum/whatIsEthereum';
import * as accountsAndKeys from './stages/ethereum/accountsAndKeys';
import * as transactionsAndGas from './stages/ethereum/transactionsAndGas';
import * as nodesAndClients from './stages/ethereum/nodesAndClients';
import * as theEvm from './stages/ethereum/theEvm';
import * as consensusProofOfStake from './stages/ethereum/consensusProofOfStake';
import * as tokensAndStandards from './stages/ethereum/tokensAndStandards';
import * as dappsAndOracles from './stages/ethereum/dappsAndOracles';

import * as whatIsCanton from './stages/canton/whatIsCanton';
import * as contractsAndParties from './stages/canton/contractsAndParties';
import * as privacyByDesign from './stages/canton/privacyByDesign';
import * as participantNodes from './stages/canton/participantNodes';
import * as synchronizersOrderingWithoutSeeing from './stages/canton/synchronizersOrderingWithoutSeeing';
import * as choicesAndControllers from './stages/canton/choicesAndControllers';
import * as composabilityAndAtomicity from './stages/canton/composabilityAndAtomicity';
import * as whereThisFits from './stages/canton/whereThisFits';

import * as whatIsSolana from './stages/solana/whatIsSolana';
import * as accountsWhereAllStateLives from './stages/solana/accountsWhereAllStateLives';
import * as programsStatelessExecutableAccounts from './stages/solana/programsStatelessExecutableAccounts';
import * as transactionsAndInstructions from './stages/solana/transactionsAndInstructions';
import * as rentAndOwnership from './stages/solana/rentAndOwnership';
import * as crossProgramInvocation from './stages/solana/crossProgramInvocation';
import * as consensusProofOfHistoryTowerBft from './stages/solana/consensusProofOfHistoryTowerBft';
import * as validatorsClustersAndAnchor from './stages/solana/validatorsClustersAndAnchor';

import * as whatIsBitcoin from './stages/bitcoin/whatIsBitcoin';
import * as keysAndAddresses from './stages/bitcoin/keysAndAddresses';
import * as theUtxoModel from './stages/bitcoin/theUtxoModel';
import * as transactionsAndFees from './stages/bitcoin/transactionsAndFees';
import * as scriptProgrammableSpending from './stages/bitcoin/scriptProgrammableSpending';
import * as blocksAndBlockchain from './stages/bitcoin/blocksAndBlockchain';
import * as miningAndProofOfWork from './stages/bitcoin/miningAndProofOfWork';
import * as p2pNetworkAndNodeValidation from './stages/bitcoin/p2pNetworkAndNodeValidation';

import * as whatIsStarknet from './stages/starknet/whatIsStarknet';
import * as cairoAndFelt252 from './stages/starknet/cairoAndFelt252';
import * as accountsAndAbstraction from './stages/starknet/accountsAndAbstraction';
import * as contractsAndStorage from './stages/starknet/contractsAndStorage';
import * as starknetTransactionsAndFees from './stages/starknet/transactionsAndFees';
import * as sequencingAndProving from './stages/starknet/sequencingAndProving';
import * as messagingAndSettlement from './stages/starknet/messagingAndSettlement';
import * as consensusAndSecurityModel from './stages/starknet/consensusAndSecurityModel';

export interface StageContent {
    tagline: string;
    blocks: StageBlock[];
}

/**
 * Maps a theory track's DB `category` and `slug` (set when the stage is
 * created in the admin panel) to its hardcoded reading content. Slugs only
 * need to be unique within a category, not globally.
 */
export const theoryContent: Record<string, Record<string, StageContent>> = {
    'ethereum-basics': {
        'what-is-ethereum': { tagline: whatIsEthereum.meta.tagline, blocks: whatIsEthereum.blocks },
        'accounts-and-keys': { tagline: accountsAndKeys.meta.tagline, blocks: accountsAndKeys.blocks },
        'transactions-and-gas': { tagline: transactionsAndGas.meta.tagline, blocks: transactionsAndGas.blocks },
        'nodes-and-clients': { tagline: nodesAndClients.meta.tagline, blocks: nodesAndClients.blocks },
        'the-evm': { tagline: theEvm.meta.tagline, blocks: theEvm.blocks },
        'consensus-proof-of-stake': { tagline: consensusProofOfStake.meta.tagline, blocks: consensusProofOfStake.blocks },
        'tokens-and-standards': { tagline: tokensAndStandards.meta.tagline, blocks: tokensAndStandards.blocks },
        'dapps-and-oracles': { tagline: dappsAndOracles.meta.tagline, blocks: dappsAndOracles.blocks },
    },
    'canton': {
        'what-is-canton': { tagline: whatIsCanton.meta.tagline, blocks: whatIsCanton.blocks },
        'contracts-and-parties': { tagline: contractsAndParties.meta.tagline, blocks: contractsAndParties.blocks },
        'privacy-by-design': { tagline: privacyByDesign.meta.tagline, blocks: privacyByDesign.blocks },
        'participant-nodes': { tagline: participantNodes.meta.tagline, blocks: participantNodes.blocks },
        'synchronizers-ordering-without-seeing': { tagline: synchronizersOrderingWithoutSeeing.meta.tagline, blocks: synchronizersOrderingWithoutSeeing.blocks },
        'choices-and-controllers': { tagline: choicesAndControllers.meta.tagline, blocks: choicesAndControllers.blocks },
        'composability-and-atomicity': { tagline: composabilityAndAtomicity.meta.tagline, blocks: composabilityAndAtomicity.blocks },
        'where-this-fits': { tagline: whereThisFits.meta.tagline, blocks: whereThisFits.blocks },
    },
    'solana': {
        'what-is-solana': { tagline: whatIsSolana.meta.tagline, blocks: whatIsSolana.blocks },
        'accounts-where-all-state-lives': { tagline: accountsWhereAllStateLives.meta.tagline, blocks: accountsWhereAllStateLives.blocks },
        'programs-stateless-executable-accounts': { tagline: programsStatelessExecutableAccounts.meta.tagline, blocks: programsStatelessExecutableAccounts.blocks },
        'transactions-and-instructions': { tagline: transactionsAndInstructions.meta.tagline, blocks: transactionsAndInstructions.blocks },
        'rent-and-ownership': { tagline: rentAndOwnership.meta.tagline, blocks: rentAndOwnership.blocks },
        'cross-program-invocation': { tagline: crossProgramInvocation.meta.tagline, blocks: crossProgramInvocation.blocks },
        'consensus-poh-tower-bft': { tagline: consensusProofOfHistoryTowerBft.meta.tagline, blocks: consensusProofOfHistoryTowerBft.blocks },
        'validators-clusters-and-anchor': { tagline: validatorsClustersAndAnchor.meta.tagline, blocks: validatorsClustersAndAnchor.blocks },
    },
    'bitcoin-basics': {
        'what-is-bitcoin': { tagline: whatIsBitcoin.meta.tagline, blocks: whatIsBitcoin.blocks },
        'keys-and-addresses': { tagline: keysAndAddresses.meta.tagline, blocks: keysAndAddresses.blocks },
        'the-utxo-model': { tagline: theUtxoModel.meta.tagline, blocks: theUtxoModel.blocks },
        'transactions-and-fees': { tagline: transactionsAndFees.meta.tagline, blocks: transactionsAndFees.blocks },
        'script-programmable-spending': { tagline: scriptProgrammableSpending.meta.tagline, blocks: scriptProgrammableSpending.blocks },
        'blocks-and-the-blockchain': { tagline: blocksAndBlockchain.meta.tagline, blocks: blocksAndBlockchain.blocks },
        'mining-and-proof-of-work': { tagline: miningAndProofOfWork.meta.tagline, blocks: miningAndProofOfWork.blocks },
        'the-p2p-network-and-node-validation': { tagline: p2pNetworkAndNodeValidation.meta.tagline, blocks: p2pNetworkAndNodeValidation.blocks },
    },
    'starknet-basics': {
        'what-is-starknet': { tagline: whatIsStarknet.meta.tagline, blocks: whatIsStarknet.blocks },
        'cairo-and-felt252': { tagline: cairoAndFelt252.meta.tagline, blocks: cairoAndFelt252.blocks },
        'accounts-and-abstraction': { tagline: accountsAndAbstraction.meta.tagline, blocks: accountsAndAbstraction.blocks },
        'contracts-and-storage': { tagline: contractsAndStorage.meta.tagline, blocks: contractsAndStorage.blocks },
        'transactions-and-fees': { tagline: starknetTransactionsAndFees.meta.tagline, blocks: starknetTransactionsAndFees.blocks },
        'sequencing-and-proving': { tagline: sequencingAndProving.meta.tagline, blocks: sequencingAndProving.blocks },
        'l1-l2-messaging-and-settlement': { tagline: messagingAndSettlement.meta.tagline, blocks: messagingAndSettlement.blocks },
        'consensus-and-the-security-model': { tagline: consensusAndSecurityModel.meta.tagline, blocks: consensusAndSecurityModel.blocks },
    },
};

export const getStageContent = (category: string, slug: string): StageContent | undefined =>
    theoryContent[category]?.[slug];

/**
 * Categories and slugs that actually have hardcoded content above. The admin
 * panel uses these to constrain stage creation/editing so a stage can't be
 * saved with a category/slug pair that has no matching reading content.
 */
export const getAvailableCategories = (): string[] => Object.keys(theoryContent);

export const getAvailableSlugs = (category: string): string[] =>
    Object.keys(theoryContent[category] ?? {});
